import { useEffect, useState } from 'react';
import axiosInstance from '../api/axiosInstance';

const baseUrl = process.env.REACT_APP_API_URL;

interface Precheck {
  id: number;
  reg: string;
  srv: string[];
  genInfo: {
    genComp: string; // company
    genAddr: string; // address
    genDirName: string; // directorName
    genDirEmail: string; // directorEmail
    genDirAddr: string; // directorAddress
    genTaxID: string; // federalTaxID
    genDesc: string; // description
    genPrice: string; // pricing
    genUrl: string; // urls
    genUser?: string; // username (optional, legacy)
    genPass?: string; // password (optional, legacy)
    genFullName: string; // fullname
    genEmail: string; // email
    genSkype?: string; // skype (optional)
    genPhone?: string; // phone (optional)
    genRefSource: string; // referralSource
  };
  ccpInfo: {
    ccpFulfill: string; // fullfillment
    ccpCallCtr: string; // callcenter
    ccpLic: string; // license
    ccpLicJur: string; // licenseJurisdiction
    ccpBankAcq: string; // bankAcquirers
    ccpCurrMCC: string; // currentMCC
    ccpAvgTxVal: string; // averageTransactionValue
    ccpCurrMonthVol: string; // currentMonthlyVolume
    ccpExpMonthVol: string; // expectedMonthlyVolume
    ccpDescLine1: string; // billingDescriptorLine1
    ccpDescLine2: string; // billingDescriptorLine2
    ccpPrefGateway: string; // preferredGateway
    ccpProcCurr?: string; // processingCurrenciesNeeded
    ccpSettleCurr?: string; // settlementCurrenciesNeeded
    ccpTargetMarkets?: string; // targetMarkets
  };
  apInfo: {
    apRegNum: string; // Company registration number
    apUsage: string; // Foreseen Main Usage
    apProjVolume: string; // Projected monthly Total Volume
    apProjCount: string; // Projected monthly Transaction Count
    apAvgTxValue: string; // Average Transaction value
    apCardCount: string; // How many cards
    apCurrencies: string; // Currencies required
    apProduct: string[]; // Selected products
  };
  wlInfo: {
    wlExpMonthVol: string; // Expected monthly processing volume
    wlExpTxCount: string; // Expected monthly transaction count
    wlAvgTxVal: string; // Average Transaction Value
    wlLicense: string; // Do you hold a license specific to your business
    wlLicJur: string; // License Jurisdiction
    wlGatewayFeatures: string[]; // Needed Gateway Features
    wlOtherGateway: string; // wlOtherGateway
    wlTargetMarkets: string; // Target Markets
    wlBankAcquirers: string; // Needed Bank Acquirers
    wlAltBilling: string; // Needed Alternative Billing Solutions
    wlOtherRemarks: string; // Other Remarks
  };
  chInfo: {
    chPayee: string; // Payee name (as it appears on bank account)
    chClient: string; // Client name (as it appears on processing statement)
    chCustEmail: string; // Customer service email
    chMktDesc: string; // Describe how your product or service is marketed and sold
    chCompHandling: string; // Explain your customer service department
    chRefundPol: string; // Refund policy
    chAchProvider: string; // ACH provider
    chAchName: string; // ACH customer service manager name
    chAchPhone: string; // ACH phone
    chAchEmail: string; // ACH email
    chHighTxAmt: string; // Highest transaction amount
    chAvgTxAmt: string; // Average transaction amount
    chMaxTxPerDay: string; // Max transactions per day
    chReqMonthLimit: string; // Monthly dollar limit
    chRecTxPerc: string; // Recurring transactions %
    chRefundCap: string; // Refund capability
    chAchCreditCap: string; // ACH credit capability
  };
  cbInfo: {
    cbBusinessAddr: string; // Business address
    cbPEP: string; // PEP
    cbMainClients: string; // Main clients
    cbFromCountry: string; // From country
    cbCurrency: string; // Currency
    cbMonthlyVol: string; // Monthly volume
    cbNumPayments: string; // Number of payments
    cbIncomingFunds: string; // Incoming funds activity
    cbToCountry: string; // To country
    cbOutgoingFunds: string; // Outgoing funds activity
  };
  incInfo: {
    incSuggestedName: string; // Suggested name
    incOfficeInUSA: string; // Office in USA
    incCurrentMerchantName: string; // Merchant name
    incExistingAddress: string; // Existing address
    incCompanyDescription: string; // Company description
    incMonthlyVolume: string; // Monthly processing volume
    incAvgTransactionValue: string; // Avg transaction value
    incBillingDescriptor1: string; // Billing descriptor line 1
    incCityField: string; // City field
    incBOName1: string; // Beneficial owner 1 name
    incBONationality1: string; // BO 1 nationality
    incBOAddress1: string; // BO 1 address
    incBOShares1: string; // BO 1 shares
    incBOName2?: string; // BO 2 name (optional)
    incBONationality2?: string; // BO 2 nationality (optional)
    incBOAddress2?: string; // BO 2 address (optional)
    incBOShares2?: string; // BO 2 shares (optional)
    incDirectorName1: string; // Director 1 name
    incDirectorNationality1: string; // Director 1 nationality
    incDirectorName2?: string; // Director 2 name (optional)
    incDirectorNationality2?: string; // Director 2 nationality (optional)
  };
  abInfo: {
    abProduct: string[]; // Selected products
    abProjectedVolume: string; // Projected volume
    abProjectedTxCount: string; // Projected transactions
    abLowestTxAmount: string; // Lowest transaction amount
    abHighestTxAmount: string; // Highest transaction amount
    abAvgTxAmount: string; // Avg transaction amount
    abTargetMarkets: string; // Target markets
    abSalesTurnover: string; // Last year's turnover
  };
  createdAt: string;
}

const usePrecheck = (id?: string) => {
  const [precheck, setPrecheck] = useState<Precheck | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrecheck = async () => {
      if (!id) return;
      try {
        const response = await axiosInstance.get(`${baseUrl}/admin/precheck/${id}`);
        setPrecheck(response.data);
      } catch (err) {
        setError('Error fetching precheck data');
        console.error('Error fetching precheck:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrecheck();
  }, [id]);

  return { precheck, loading, error };
};

export default usePrecheck;
